import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { CompatibilityForm, type CompatibilityData } from './compatibility/CompatibilityForm';
import { CompatibilityResult } from './compatibility/CompatibilityResult';
import { LoadingSpinner } from './common/Loading';
import { ErrorMessage } from './common/Error';
import { toast } from 'sonner';

// Análise estática modelo (IA volta quando a análise migrar pro server).
// Gera conteúdo determinístico a partir dos dados do formulário.
function signFromBirthDate(birthDate?: string): string {
  if (!birthDate) return 'signo não informado';
  const month = Number(birthDate.slice(5, 7));
  const day = Number(birthDate.slice(8, 10));
  const boundaries: Array<[number, number, string]> = [
    [1, 20, 'Capricórnio'], [2, 19, 'Aquário'], [3, 20, 'Peixes'],
    [4, 20, 'Áries'], [5, 21, 'Touro'], [6, 21, 'Gêmeos'],
    [7, 22, 'Câncer'], [8, 23, 'Leão'], [9, 23, 'Virgem'],
    [10, 23, 'Libra'], [11, 22, 'Escorpião'], [12, 22, 'Sagitário'],
  ];
  for (const [monthBoundary, dayBoundary, name] of boundaries) {
    if (month < monthBoundary || (month === monthBoundary && day < dayBoundary)) {
      return name;
    }
  }
  return 'Capricórnio';
}

function buildStaticReport(data: CompatibilityData): string {
  const sign1 = signFromBirthDate(data.person1.birthDate);
  const sign2 = signFromBirthDate(data.person2.birthDate);
  const sameSign = sign1 === sign2;

  return [
    `## ${sign1} + ${sign2}`,
    '',
    `**Pessoa 1:** ${data.person1.name} · **Pessoa 2:** ${data.person2.name}`,
    '',
    '### O encontro',
    sameSign
      ? `${sign1} e ${sign2} dividem o mesmo temperamento: a relação começa com uma sensação imediata de reconhecimento. Vocês falam a mesma língua emocional, o que acelera a intimidade.`
      : `${sign1} e ${sign2} são temperamentos distintos que se completam: onde um é direto, o outro respeita a pausa. A atração nasce justamente do que falta a cada um.`,
    '',
    '### Onde brilha',
    sameSign
      ? '- Cumplicidade instantânea; planos nascem maduros.'
      : '- Um puxa, o outro ancora: raramente os dois perdem o chão ao mesmo tempo.',
    '',
    '### Pontos de atenção',
    '- Expectativas não se adivinham: verbalize.',
    '- Rotina e dinheiro merecem conversa antes de virar tema.',
    '',
    '### Síntese',
    `Existe base real aqui. ${data.person1.name.split(' ')[0]} e ${data.person2.name.split(' ')[0]} têm material para uma parceria firme — com conversa clara, a relação amadurece bem.`,
  ].join('\n');
}

export const CompatibilityPage: React.FC = () => {
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnalysis = async (data: CompatibilityData) => {
    setLoading(true);
    setError(null);

    try {
      const report = buildStaticReport(data);
      setResult(report);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar análise';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 pt-8 min-h-[calc(100vh-4rem)]">
      <h2 className="text-2xl font-bold text-purple-200 mb-6 flex items-center">
        <Heart className="w-6 h-6 mr-2 text-purple-300" />
        Análise de Compatibilidade
      </h2>

      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="max-w-2xl mx-auto">
          <ErrorMessage message={error} />
          <button
            onClick={() => setError(null)}
            className="mt-4 btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      ) : !result ? (
        <CompatibilityForm onResult={handleAnalysis} />
      ) : (
        <div className="space-y-6">
          <CompatibilityResult content={result} />
          <button
            onClick={() => setResult(null)}
            className="btn-primary"
          >
            Nova Análise
          </button>
        </div>
      )}
    </div>
  );
};